import React from 'react';
import { Box, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { StreakSummary } from '../../types/ranked';

interface StreakFlameProps {
  streak: StreakSummary;
}

/**
 * Daily streak counter.
 *
 * Dimmed when the student has not played yet today, which reads as "still alive,
 * but act" rather than a broken streak — a streak survives until a full day is
 * missed.
 */
const StreakFlame: React.FC<StreakFlameProps> = ({ streak }) => {
  const theme = useTheme();
  const color = theme.palette.warning.main;
  const isCold = !streak.activeToday;

  const tooltip = streak.activeToday
    ? `${streak.current}-day streak. Longest: ${streak.longest}.`
    : `${streak.current}-day streak — play today to keep it going. Longest: ${streak.longest}.`;

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          px: 1.2,
          py: 0.5,
          borderRadius: 4,
          backgroundColor: alpha(color, isCold ? 0.06 : 0.12),
          color: isCold ? theme.palette.text.secondary : theme.palette.warning.dark,
          opacity: isCold ? 0.85 : 1
        }}
      >
        <LocalFireDepartmentIcon sx={{ fontSize: 18, color: isCold ? 'inherit' : color }} />
        <Typography
          component="span"
          sx={{
            fontFamily: 'monospace',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 700,
            fontSize: '0.8rem'
          }}
        >
          {streak.current}
        </Typography>
        <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
          day{streak.current === 1 ? '' : 's'}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default StreakFlame;
